<template class="content-border row">
  <fragment>
    <page-head
      small
      :back-route-path="`/bankruptcy/bankruptcy-file/${requestObjectKey}`"
      show-back
      :back-text="local.common.return"
      class="page-head mt-3"
    >
      <div class="head-wings">
        <div
          v-show="$vuetify.breakpoint.lgAndUp"
          class="pr-16"
        >
          <p class="wing-1">
            {{ local.bankruptcy.subRequests.createNewSubRequest }}
            <span
              :class="{
                'status-ok': true,
              }"
              class="px-2"
            />
          </p>
        </div>
      </div>
    </page-head>

    <page-content small>
      <!-- Main content -->
      <template v-if="isUserLoaded">
        <button-category
          :key="categoryObjectKey"
          :category-key="categoryObjectKey"
          class="me-0"
          :sub-request-categories="subRequestCategories"
          @selected-category="handleCategoryclick"
        >
          <template>
            <v-tab-item>
              <sub-requests-type-card
                :key="categoryObjectKey"
                :loading="loading"
                :sub-request-types="subRequestTypes"
                :sub-request-type-category="subRequestCategories"
                @select-case-request-type="handleCardClick"
              ></sub-requests-type-card>
            </v-tab-item>
          </template>
        </button-category>

        <!-- cards grid -->
        <v-row
          dense
          class="mb-6"
        >
        </v-row>
      </template>

      <naj-confirmation
        :show-modal="showRequestModel"
        :max-width="771"
        :title="local.home.tabCommon.newSubRequest"
        :show-close-btn="false"
        @cancel="
          () => {
            selectedRequestType = {};
            selectedRequestTypeId = 0;
            showRequestFullExplanation = false;
            showRequestModel = false;
            pledged = false;
          }
        "
      >
        <template slot="message-content">
          <span class="explanation">
            {{ local.bankruptcy.subRequests.newSubRequestExplanation }}
          </span>
          <v-row>
            <v-col
              v-if="showRequestFullExplanation"
              cols="12"
            >
              <naj-alert type="alert">
                <template slot="content">
                  {{ local.lawsuitDetails.caseRelatedRequestFeesNote }}
                  <a
                    :href="Urls.judicialFees"
                    target="_blank"
                    class="about-link"
                  >
                    {{ local.common.clickHere }}
                  </a>
                </template>
              </naj-alert>
            </v-col>
            <v-col
              v-if="showRequestFullExplanation"
              cols="12"
            >
              <naj-input
                :type-id="formFieldTypes.checkbox"
                :value.sync="pledged"
                :label="selectedRequestType.fullExplanation"
              ></naj-input>
            </v-col>
          </v-row>
        </template>
        <template slot="actions">
          <naj-button
            :disabled="!pledged"
            @click="openCreateNewRequest()"
          >
            {{ local.common.saveAndSend }}
          </naj-button>
        </template>
      </naj-confirmation>
    </page-content>
  </fragment>
</template>

<script>
import SubRequestsTypeCard from './sub-request-type-card.vue';
import ButtonCategory from './buttonCategory.vue';
import { createNamespacedHelpers } from 'vuex';
import {
  subRequestTypeIds,
  subRequestTypesEnum,
  formFieldTypes,
  subRequestType,
  subRequestStatus,
} from '@/modules/bankruptcy/helpers/enum';
import { userType } from '@/helpers/enum';
import { Urls } from '@/modules/judicialFees/helpers/enum';

const { mapActions, mapState } = createNamespacedHelpers('BankruptcyModule');

export default {
  name: 'SubRequestTypesList',
  components: {
    SubRequestsTypeCard,
    ButtonCategory,
  },
  props: {
    requestObjectKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      userLoading: false,
      subRequestCategories: [],
      subRequestTypes: [],
      subRequestTypeIds,
      subRequestTypesEnum,
      showRequestFullExplanation: false,
      pledged: false,
      selectedRequestType: {},
      selectedRequestTypeId: 0,
      showRequestModel: false,
      subRequestType,
      subRequestStatus,
      userType,
      Urls,
      categoryObjectKey: null,
    };
  },
  computed: {
    ...mapState({ userInfo: state => state.currentUser }),
    isUserLoaded() {
      return this.userInfo && Object.keys(this.userInfo).length > 0;
    },
    isOrganizationUser() {
      return this.userInfo?.isOrganizationUser;
    },
    requestTypes() {
      if (!this.isOrganizationUser) {
        return [
          ...this.subRequestTypes.filter(item =>
            [subRequestType.AddRepresentative, subRequestType.VerdictObjectionRequest].includes(item.id)
          ),
          subRequestTypesEnum.GeneralRequest,
        ].map(item => ({
          id: item.id,
          name: item.name,
          fullExplanation: item.fullExplanation,
          componentName: item.customComponentName,
        }));
      } else if (this.userInfo.isBankruptcyTrustee) {
        return [subRequestTypesEnum.GeneralRequest].map(item => ({
          id: item.id,
          name: item.name,
          fullExplanation: item.fullExplanation,
          componentName: item.customComponentName,
        }));
      } else {
        return [
          ...this.subRequestTypes.filter(item => item.id == subRequestType.VerdictObjectionRequest),
          subRequestTypesEnum.GeneralRequest,
        ].map(item => ({
          id: item.id,
          name: item.name,
          fullExplanation: item.fullExplanation,
          componentName: item.customComponentName,
        }));
      }
    },
    formFieldTypes() {
      return formFieldTypes;
    },
  },
  watch: {
    userInfo: {
      deep: true,
      immediate: true,
      handler() {
        if (this.isUserLoaded) {
          this.loadSubRequestTypes();
        }
      },
    },
  },
  async mounted() {
    this.userLoading = true;
    await this.getBankruptcyUserInfo();
    this.userLoading = false;
  },
  methods: {
    ...mapActions([
      'getBankruptcyUserInfo',
      'canIssueSubRequest',
      'getValidSubRequestsForProcedure',
      'getSubRequestsTypesByCategory',
      'getSubRequestsTypesCategories',
    ]),

    async loadSubRequestTypes() {
      if (!this.isUserLoaded) return;

      this.loading = true;

      const payload = {
        objectKey: this.requestObjectKey,
        identityNumber: this.isOrganizationUser ? this.userInfo.impersonatedBySocialId : this.userInfo.socialId,
        unifiedNumber: this.userInfo.nationalRecordNumber,
        isSubRequest: false,
        categoryObjectKey: this.categoryObjectKey,
      };

      try {
        const [categoriesRes, typesRes] = await Promise.all([
          this.getSubRequestsTypesCategories(payload),
          this.getSubRequestsTypesByCategory(payload),
        ]);

        this.subRequestCategories = categoriesRes.data.map(item => ({
          ...item,
          onClick: this.handleCategoryclick,
        }));

        this.subRequestTypes = typesRes.data
          .map(item => ({
            ...item,
            onClick: this.handleCardClick,
          }))
          .filter(
            ele => ele.id !== subRequestTypesEnum.objectionRequest.id && ele.id !== subRequestTypesEnum.addRepresentative.id
          );
      } catch (error) {
        this.toaster.error(`Error loading sub-request types: ${error.message}`);
      } finally {
        this.loading = false;
      }
    },

    handleCategoryclick(objectKey) {
      this.loading = true;
      this.subRequestTypes = [];
      this.categoryObjectKey = objectKey;
      this.loadSubRequestTypes();
    },

    handleCardClick(id) {
      this.selectedRequestTypeId = id;
      this.loading = true;
      this.showRequestFullExplanation = false;
      this.pledged = false;

      this.canIssueSubRequest({
        objectKey: this.requestObjectKey,
        requestType: id,
      })
        .then((res) => {
          if (res.data) {
            this.showRequestFullExplanation = true;
            this.selectedRequestType = this.subRequestTypes.find(rt => rt.id == id);
            this.showRequestModel = true;
          }
        })
        .finally(() => {
          this.pledged = false;
          this.loading = false;
        });
    },

    openCreateNewRequest() {
      this.showRequestFullExplanation = false;
      this.$router.push({
        path: `/bankruptcy/${this.requestObjectKey}/bankruptcy-file/${this.selectedRequestType.code}`,
        query: { isInternal: true },
      });
    },

    closeModal() {
      this.showRequestModel = false;
      this.selectedRequestType = {};
      this.selectedRequestTypeId = 0;
      this.showRequestFullExplanation = false;
      this.pledged = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.content-border {
  border: 35px solid #faf9f4 !important;
}
.head-wings {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 25px;
  .r-info {
    margin-left: 40px;
  }
}
.tabs-box {
  padding: 0;
  background: #faf9f4;

  .tab-button {
    font: normal normal normal 16px/24px Almarai;
    justify-content: right;

    &.tab-active {
      border-right: 4px solid var(--app-primary-color);
      font-weight: bold;
      color: var(--app-primary-color);
    }
  }
}
.wing-1 {
  font-weight: 1000;
  font-size: 18px;
  padding: 155px;
}
.wing-2 {
  font-weight: bolder;
  font-size: 14px;
}
.tabs-section {
  min-height: 600px;
}

.status-rejected {
  color: #f44336 !important;
}

.status-ok {
  color: #29af5b;
}

.v-tab {
  background: #faf9f4;
}
.statusName {
  font-size: 14px;
}
.page-head {
  box-shadow: none !important;
  background: #fff !important;
}
.content-box {
  padding: 0px !important;
  margin-top: 0px !important;
}
</style>
